import { useState } from 'react'
import { IoChevronDown, IoChevronUp, IoAdd, IoTrendingUp, IoTrendingDown, IoRemove } from 'react-icons/io5'
import type { PlayerFoulsCardsItem } from '../../types'
import { COUNTRY_FLAGS } from '../../context/LeagueContext'

interface PlayerFoulsCardsCardProps {
  item: PlayerFoulsCardsItem
}

function formatKickoff(isoDate: string): string {
  const date = new Date(isoDate)
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  const day = days[date.getDay()]
  const hours = date.getHours().toString().padStart(2, '0')
  const mins = date.getMinutes().toString().padStart(2, '0')
  return `${day} ${hours}:${mins}`
}

function getPositionClass(position: string | null): string {
  if (!position) return ''
  const pos = position.toLowerCase()
  if (['attacker', 'forward', 'striker', 'f', 'st', 'cf', 'lw', 'rw'].includes(pos)) return 'attacker'
  if (['midfielder', 'midfield', 'm', 'cm', 'dm', 'am', 'lm', 'rm', 'cdm', 'cam'].includes(pos)) return 'midfielder'
  if (['defender', 'defence', 'd', 'cb', 'lb', 'rb', 'lwb', 'rwb'].includes(pos)) return 'defender'
  return ''
}

function getPositionLabel(position: string | null): string {
  if (!position) return '-'
  const pos = position.toLowerCase()
  if (['attacker', 'forward', 'striker', 'f', 'st', 'cf', 'lw', 'rw'].includes(pos)) return 'FWD'
  if (['midfielder', 'midfield', 'm', 'cm', 'dm', 'am', 'lm', 'rm', 'cdm', 'cam'].includes(pos)) return 'MID'
  if (['defender', 'defence', 'd', 'cb', 'lb', 'rb', 'lwb', 'rwb'].includes(pos)) return 'DEF'
  if (['goalkeeper', 'g', 'gk'].includes(pos)) return 'GK'
  return pos.substring(0, 3).toUpperCase()
}

function getProbabilityClass(prob: number): string {
  if (prob >= 60) return 'prob-high'
  if (prob >= 40) return 'prob-med'
  return 'prob-low'
}

function getRefFactorClass(factor: number): string {
  if (factor >= 1.2) return 'ref-high'
  if (factor >= 1.05) return 'ref-med'
  return 'ref-low'
}

function getCountryFlag(country: string | null): string {
  if (!country) return ''
  return COUNTRY_FLAGS[country] || ''
}

function getTrendIcon(trend: 'up' | 'down' | 'flat' | null, statType: 'fouls' | 'fouled') {
  if (!trend) return null
  const label = statType === 'fouls' ? 'fouls committed' : 'times fouled'
  if (trend === 'up') {
    return (
      <IoTrendingUp
        className="trend-icon trend-up"
        size={12}
        title={`Trending up: ${label} increasing (last 3 games > last 5 avg)`}
      />
    )
  }
  if (trend === 'down') {
    return (
      <IoTrendingDown
        className="trend-icon trend-down"
        size={12}
        title={`Trending down: ${label} decreasing (last 3 games < last 5 avg)`}
      />
    )
  }
  return (
    <IoRemove
      className="trend-icon trend-flat"
      size={12}
      title={`Stable: ${label} consistent (last 3 ≈ last 5 avg)`}
    />
  )
}

export function PlayerFoulsCardsCard({ item }: PlayerFoulsCardsCardProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [addedMarkets, setAddedMarkets] = useState<Set<string>>(new Set())
  const [showAddMenu, setShowAddMenu] = useState(false)

  const handleAddOption = (marketType: string) => {
    // TODO: Integrate with bet builder context
    setAddedMarkets((prev) => new Set(prev).add(marketType))
    setShowAddMenu(false)
  }

  return (
    <div className="player-stats-card fouls-cards-card" onClick={() => setIsExpanded(!isExpanded)}>
      <div className="player-stats-card-header">
        <div className="player-stats-card-player">
          <span className="name">{item.player_name}</span>
          <span className={`position-badge ${getPositionClass(item.position)}`}>
            {getPositionLabel(item.position)}
          </span>
        </div>
        <div className="player-stats-card-badges">
          <span className={`prob-badge ${getProbabilityClass(item.cards_conviction_pct)}`} title="Card probability">
            {item.cards_conviction_pct}%
          </span>
        </div>
      </div>

      <div className="player-stats-card-fixture">
        <span>{getCountryFlag(item.league_country)} {item.fixture_label}</span>
        <span> - {formatKickoff(item.kickoff_utc)}</span>
      </div>

      <div className="player-stats-card-stats">
        <div className="player-stats-card-stat">
          <span className="label">Fouls</span>
          <span className="value stat-with-trend">
            {item.fouls_committed_avg.toFixed(1)}
            {getTrendIcon(item.fouls_committed_trend, 'fouls')}
          </span>
        </div>
        <div className="player-stats-card-stat">
          <span className="label">Fouled</span>
          <span className="value stat-with-trend">
            {item.fouls_drawn_avg.toFixed(1)}
            {getTrendIcon(item.fouls_drawn_trend, 'fouled')}
          </span>
        </div>
        <div className="player-stats-card-stat">
          <span className="label">Tackles</span>
          <span className="value">{item.tackles_avg.toFixed(1)}</span>
        </div>
        <div className="player-stats-card-stat">
          <span className="label">Cards Avg</span>
          <span className="value">{item.cards_avg.toFixed(2)}</span>
        </div>
      </div>

      <div className="player-stats-card-stats ref-factors-row">
        <div className="player-stats-card-stat">
          <span className="label">Ref Fouls</span>
          <span className={`ref-badge ${getRefFactorClass(item.ref_foul_factor)}`}>
            {item.ref_foul_factor.toFixed(2)}x
          </span>
        </div>
        <div className="player-stats-card-stat">
          <span className="label">Ref Cards</span>
          <span className={`ref-badge ${getRefFactorClass(item.ref_card_factor)}`}>
            {item.ref_card_factor.toFixed(2)}x
          </span>
        </div>
        <div className="player-stats-card-stat">
          <span className="label">Pos Factor</span>
          <span className="value">{item.position_factor.toFixed(2)}x</span>
        </div>
      </div>

      {isExpanded && (
        <div className="player-stats-card-expanded">
          <div className="player-stats-card-stats">
            <div className="player-stats-card-stat">
              <span className="label">Season Fouls</span>
              <span className="value">{item.season_fouls_avg != null ? item.season_fouls_avg.toFixed(2) : '-'}</span>
            </div>
            <div className="player-stats-card-stat">
              <span className="label">Season Cards</span>
              <span className="value">{item.season_cards_avg != null ? item.season_cards_avg.toFixed(2) : '-'}</span>
            </div>
            <div className="player-stats-card-stat">
              <span className="label">Exp Mins</span>
              <span className="value">{item.expected_minutes != null ? Math.round(item.expected_minutes) : '-'}</span>
            </div>
            <div className="player-stats-card-stat">
              <span className="label">Role</span>
              <span className="value">{item.role || '-'}</span>
            </div>
          </div>
          <div className="player-stats-card-why">
            <strong>Fouls:</strong> {item.why_fouls || 'No analysis'}
          </div>
          <div className="player-stats-card-why">
            <strong>Cards:</strong> {item.why_cards || 'No analysis'}
          </div>
        </div>
      )}

      <div className="player-stats-card-actions">
        <button
          className="expand-toggle"
          onClick={(e) => {
            e.stopPropagation()
            setIsExpanded(!isExpanded)
          }}
        >
          {isExpanded ? <IoChevronUp size={16} /> : <IoChevronDown size={16} />}
        </button>
        <div className="player-add-dropdown">
          <button
            className="player-stats-add-btn"
            onClick={(e) => {
              e.stopPropagation()
              setShowAddMenu(!showAddMenu)
            }}
          >
            <IoAdd size={14} /> Add <IoChevronDown size={12} />
          </button>
          {showAddMenu && (
            <div className="dropdown-menu" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => handleAddOption('fouls')}
                disabled={addedMarkets.has('fouls')}
              >
                Fouls O/U
              </button>
              <button
                onClick={() => handleAddOption('fouled')}
                disabled={addedMarkets.has('fouled')}
              >
                To Be Fouled
              </button>
              <button
                onClick={() => handleAddOption('card')}
                disabled={addedMarkets.has('card')}
              >
                To Be Carded
              </button>
              {item.tackles_market_id && (
                <button
                  onClick={() => handleAddOption('tackles')}
                  disabled={addedMarkets.has('tackles')}
                >
                  Tackles O/U
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
